import { useCallback, useSyncExternalStore } from 'react';

/**
 * The What's new badge (#2109): a dot on the sidebar entry until this browser
 * opened the latest release. `localStorage['whats-new-seen']` holds the id of
 * the latest release seen; opening the page sets it. Per browser, not per user.
 */
export const WHATS_NEW_SEEN_KEY = 'whats-new-seen';

/** Fired in this tab when the seen id changes; `storage` covers the other tabs. */
const SEEN_EVENT = 'whats-new-seen-change';

// Written by `pnpm guide:build` next to the bundles: only the latest id, so the
// badge never loads a whole guide bundle. Missing before the first build.
const LATEST = import.meta.glob('./generated/whats-new/latest.json', { eager: true, import: 'default' }) as Record<
  string,
  { id: string; date: string } | null
>;

/** The id of the newest release note (`whats-new.<yyyy-mm-dd>`), if any. */
export function latestReleaseId(): string | undefined {
  return Object.values(LATEST)[0]?.id ?? undefined;
}

export function readSeenRelease(): string | null {
  try {
    return window.localStorage.getItem(WHATS_NEW_SEEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Is `latest` newer than what this browser has seen? Ids carry the date, so a
 * plain string comparison orders them; an older seen id still shows the dot.
 */
export function isReleaseUnseen(latest: string | undefined, seen: string | null): boolean {
  return Boolean(latest) && (!seen || seen < (latest as string));
}

/** Record `id` as seen; never moves the mark back to an older release. */
export function markReleaseSeen(id: string): void {
  if (!isReleaseUnseen(id, readSeenRelease())) return;
  try {
    window.localStorage.setItem(WHATS_NEW_SEEN_KEY, id);
  } catch {
    return;
  }
  window.dispatchEvent(new Event(SEEN_EVENT));
}

function subscribe(onChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === WHATS_NEW_SEEN_KEY) onChange();
  };
  window.addEventListener(SEEN_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(SEEN_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}

/** True while a release newer than the one this browser last opened exists. */
export function useWhatsNewUnseen(latest: string | undefined = latestReleaseId()): boolean {
  const getSnapshot = useCallback(() => isReleaseUnseen(latest, readSeenRelease()), [latest]);
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
